import { gsap } from 'gsap';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import { motion } from 'motion/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavigateOptions, To, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@column/column-ui-kit';

import { HintLink, Logo, Panel, animateLogo } from '../_components';
import { ROUTE as APP_ROUTE, ROUTE_PREFIX as APP_ROUTE_PREFIX } from '~/app/routes';
import { useQueryParams } from '~/hooks';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { LoginAuthMethod } from '~/lib/session';
import { ROUTE as PUBLIC, ROUTE } from '~/public/routes';
import {
  ChallengeMFAResponse,
  ChallengeName,
  DashboardLoginResponse,
  EmailConfirmationStatus,
  MFAStatus,
  PlaidParams,
} from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Divider, Flex, Hint, Paragraph, Text } from '~/styles';

import { MultifactorAuthForm, PasskeyAuthButton, PasswordAuthForm } from './_partials';

interface PageLoginProps {
  isAuthFlow?: boolean;
}

export type LoginQueryParams = {
  [key: string]: string;
  redirect: string;
};

enum LoginView {
  PASSWORD = 'password',
  MFA = 'mfa',
  EMAIL_CONFIRMATION = 'email_confirmation',
}

interface PassswordState {
  view: LoginView.PASSWORD;
}

interface MultifactorState {
  view: LoginView.MFA;
  challengeName: ChallengeName;
  phoneNumber?: string;
  sessionId: string;
}

interface EmailConfirmationState {
  view: LoginView.EMAIL_CONFIRMATION;
}

type LoginState = PassswordState | MultifactorState | EmailConfirmationState;

const LOGIN_EMAIL_CONFIRMATION_REQUIRED_ERROR_CODE = 'login_email_confirmation_required';

const AdditionalLogo = styled(Logo)`
  margin-left: -12px;
  transform-origin: 0 0;
  overflow: visible;

  & > path {
    --logo-path-opacity: 0;
    --logo-path-x: 16px;
    --logo-path-y: 24px;
    opacity: var(--logo-path-opacity);
    transform: translate(var(--logo-path-x), var(--logo-path-y)) translateZ(0);
  }
`;

const HiddenLogo = styled(Logo)`
  opacity: 0;
`;

const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
`;

const normalizeRedirectUrl = (url: string | null): string | undefined => {
  if (!url) {
    return undefined;
  }

  const isAbsoluteUrl = (testUrl: string): boolean => /^(https?:\/\/)/i.test(testUrl);
  const getRelativeUrl = (testUrl: string): string => (testUrl.startsWith('/') ? testUrl : `/${testUrl}`);

  if (isAbsoluteUrl(url)) {
    return url;
  }

  return getRelativeUrl(url);
};

export const PageLogin: React.FC<PageLoginProps> = ({ isAuthFlow }) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const passkeyAuthEnabled = useFeatureFlag(FeatureFlag.EnablePasskeyAuth);
  const { setSession, signIn, addParam, params } = useSessionStore();
  const { getQueryParam, hasQueryParam } = useQueryParams<LoginQueryParams>();
  const logoWrapperRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectUrl = normalizeRedirectUrl(getQueryParam('redirect'));

  const [loginState, setLoginState] = useState<LoginState>({
    view: LoginView.PASSWORD,
  });

  const navigateWithAnimation = useCallback((to: To, options?: NavigateOptions) => {
    const logo = logoRef.current;
    const logoWrapper = logoWrapperRef.current;
    const panel = panelRef.current;

    if (!logo || !logoWrapper || !panel) {
      navigate(to, options);
      return;
    }

    // Lock the panel dimensions to prevent layout shifts during animation
    const panelRect = panel.getBoundingClientRect();
    panel.style.width = `${panelRect.width}px`;
    panel.style.height = `${panelRect.height}px`;

    animateLogo(logo, logoWrapper, panel, gsap, MotionPathPlugin, () => navigate(to, options));
  }, []);

  const onLogin = useCallback(
    (sessionId: string, dashboardUserId: string, mfaStatus: MFAStatus, loginAuthMethod: LoginAuthMethod) => {
      setSession(sessionId, dashboardUserId, { loginAuthMethod, mfaStatus });

      signIn()
        .then(() => {
          if (isAuthFlow && params) {
            navigate(PUBLIC.PLAID_SELECT);
            return;
          }

          const destinationUrl = redirectUrl || APP_ROUTE.ROOT;

          if (destinationUrl.startsWith(APP_ROUTE_PREFIX)) {
            navigateWithAnimation(destinationUrl, {
              state: { from: 'auth' },
            });
            return;
          }
          navigate(destinationUrl, {
            state: { from: 'auth' },
          });
        })
        .catch((error) => {
          addDangerNotification({
            content: error.message,
            display: 'page',
          });
        });
    },
    [navigateWithAnimation, isAuthFlow, params, redirectUrl]
  );

  const onAuthSuccess = useCallback(
    (response: DashboardLoginResponse, loginAuthMethod: LoginAuthMethod) => {
      if (response.challengeName && response.challengeName !== ChallengeName.CHALLENGE_NONE) {
        setLoginState({
          view: LoginView.MFA,
          challengeName: response.challengeName,
          phoneNumber: response.phoneNumber,
          sessionId: response.sessionId,
        });
        return;
      }

      if (response.emailConfirmationStatus === EmailConfirmationStatus.UNVERIFIED) {
        setLoginState({ view: LoginView.EMAIL_CONFIRMATION });
        return;
      }

      onLogin(response.sessionId, response.dashboardUserId, response.mfaStatus, loginAuthMethod);
    },
    [onLogin]
  );

  const onMultifactorSuccess = useCallback(
    (sessionId: string, response: ChallengeMFAResponse) => {
      if (response.emailConfirmationStatus === EmailConfirmationStatus.UNVERIFIED) {
        setLoginState({ view: LoginView.EMAIL_CONFIRMATION });
        return;
      }

      onLogin(sessionId, response.dashboardUserId, response.mfaStatus, LoginAuthMethod.PASSWORD);
    },
    [onLogin]
  );

  const onPasswordAuthError = useCallback((error: unknown): boolean => {
    if (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      typeof error.code === 'string' &&
      error.code === LOGIN_EMAIL_CONFIRMATION_REQUIRED_ERROR_CODE
    ) {
      setLoginState({ view: LoginView.EMAIL_CONFIRMATION });
      return true;
    }

    return false;
  }, []);

  useEffect(() => {
    Object.keys(PlaidParams).forEach((key: string) => {
      if (hasQueryParam(key)) {
        addParam(key, getQueryParam(key) as string | undefined);
      }
    });
  }, [location.search]);

  return (
    <>
      {!isAuthFlow && (
        <LogoWrapper ref={logoWrapperRef}>
          <AdditionalLogo size="24px" variant="full" ref={logoRef} />
        </LogoWrapper>
      )}

      <Panel ref={panelRef}>
        {!isAuthFlow && <HiddenLogo size="24px" />}
        {loginState.view === LoginView.PASSWORD && (
          <Flex flexDirection="column" gap={16}>
            <PasswordAuthForm
              showForgotPassword={!isAuthFlow}
              redirectUrl={redirectUrl}
              onError={onPasswordAuthError}
              onSuccess={(response) => onAuthSuccess(response, LoginAuthMethod.PASSWORD)}
            />
            {passkeyAuthEnabled && (
              <>
                <Divider>or</Divider>
                <PasskeyAuthButton onSuccess={(response) => onAuthSuccess(response, LoginAuthMethod.PASSKEY)} />
              </>
            )}
          </Flex>
        )}
        {loginState.view === LoginView.MFA && (
          <MultifactorAuthForm
            challengeName={loginState.challengeName}
            phoneNumber={loginState.phoneNumber}
            onCancel={() => setLoginState({ view: LoginView.PASSWORD })}
            onSuccess={(response) => onMultifactorSuccess(loginState.sessionId, response)}
          />
        )}
        {loginState.view === LoginView.EMAIL_CONFIRMATION && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <Flex flexDirection="column" gap={8}>
              <Text size={16} weight="semibold">
                Check your email
              </Text>
              <Paragraph>We've sent a sign-in link to your inbox to make sure it's you.</Paragraph>
              <Flex justifyContent="center" mt={24}>
                <HintLink onClick={() => setLoginState({ view: LoginView.PASSWORD })}>Back to login</HintLink>
              </Flex>
            </Flex>
          </motion.div>
        )}
        {loginState.view === LoginView.PASSWORD &&
          !isAuthFlow &&
          (passkeyAuthEnabled ? (
            <Hint>
              Don't have a Column account?{' '}
              <Link
                className="inline text-gray-500 no-underline transition-colors hover:text-gray-600"
                to={ROUTE.REGISTER}
              >
                Create one
              </Link>
            </Hint>
          ) : (
            <Flex flexDirection="column" gap={16}>
              <Paragraph size="small">Don't have a Column account?</Paragraph>
              <Button variant="secondary" onClick={() => navigate(ROUTE.REGISTER)}>
                Create New Account
              </Button>
            </Flex>
          ))}
      </Panel>
    </>
  );
};
